"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence, motion } from "motion/react";
import { ArrowRight, X } from "lucide-react";
import { Link } from "@/i18n/routing";
import { Button } from "@/components/ui/button";
import { INTERNET_COMPARE_LIMIT, type InternetPlan } from "@/lib/fetch/internet";
import {
  getInternetCompareHref,
  useInternetCompare,
} from "./use-internet-compare";

type Props = {
  byId: Map<string, InternetPlan>;
};

/**
 * Fixed bottom bar listing the plans picked for comparison, with the jump to
 * the compare page. It only appears once something is picked.
 */
const InternetCompareTray: React.FC<Props> = ({ byId }) => {
  const t = useTranslations("internet");
  const compare = useInternetCompare();
  const selected = compare.ids
    .map((id) => byId.get(id))
    .filter((plan): plan is InternetPlan => Boolean(plan));
  const visible = selected.length > 0;

  return (
    <>
      {visible && <div aria-hidden className="h-28 sm:h-24" />}
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.25)] backdrop-blur"
            role="region"
            aria-label={t("tray.label")}
          >
            <div className="container flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-col gap-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("tray.title", {
                    count: selected.length,
                    limit: INTERNET_COMPARE_LIMIT,
                  })}
                </p>
                <ul className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none]">
                  {selected.map((plan) => (
                    <li key={plan.id} className="flex-none">
                      <button
                        type="button"
                        onClick={() => compare.remove(plan.id)}
                        title={t("tray.remove", {
                          plan: `${plan.provider.name} ${plan.name}`,
                        })}
                        className="inline-flex min-h-8 max-w-[14rem] items-center gap-1.5 rounded-full border border-border bg-background px-3 text-xs font-semibold transition-colors hover:border-zinc-300"
                      >
                        <span className="truncate">
                          {plan.provider.name} · {plan.name}
                        </span>
                        <X className="size-3 flex-none" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-none items-center gap-2">
                <button
                  type="button"
                  onClick={compare.clear}
                  className="min-h-10 px-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                >
                  {t("tray.clear")}
                </button>
                <Button
                  asChild
                  variant="secondary"
                  className="flex-1 sm:flex-none"
                >
                  <Link
                    href={getInternetCompareHref(compare.ids)}
                    title={t("tray.compare-title")}
                    aria-disabled={selected.length < 2}
                    className={
                      selected.length < 2 ? "pointer-events-none opacity-60" : ""
                    }
                  >
                    {selected.length < 2
                      ? t("tray.pick-another")
                      : t("tray.compare", { count: selected.length })}
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default InternetCompareTray;
